import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from '../../../../utils/axios';
import { errorToast, successToastTopRight } from '../../../../utils/toasts/toasts';

const initialState = {
    tickets: [],
    ticket: null,
    loading: false,
    error: null,
    allTicketsIds: [],
    isTicketPageOpen: false,
    showTicketForm: false,
    showTicketList: false
};

const ticketSlice = createSlice({
    name: 'tickets',
    initialState,
    reducers: {
        redirectToTicket: (state, action) => {
            const { isTicketPageOpen } = action.payload;
            state.isTicketPageOpen = isTicketPageOpen || initialState.isTicketPageOpen;
        },
        showTicketFormUi: (state, action) => {
            const { showTicketForm } = action.payload;
            state.showTicketForm = showTicketForm || initialState.showTicketForm;
        },
        showTicketListUi: (state, action) => {
            const { showTicketList } = action.payload;
            state.showTicketList = showTicketList || initialState.showTicketList;
        },
        setSelectedTicket: (state, action) => {
            state.ticket = action.payload; // Update the selected ticket
        },
        clearSelectedTicketState: (state) => {
            state.ticket = null; // Clear the selected ticket state
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(createTicketByEventId.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(createTicketByEventId.fulfilled, (state, action) => {
                state.loading = false;
                state.tickets.push(action.payload?.data);
                console.log('Ticket created successfully:', action.payload);
            })
            .addCase(createTicketByEventId.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message;
                console.error('Failed to create ticket:', action.error.message);
            });

        builder
            .addCase(getTicketsByEventId.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getTicketsByEventId.fulfilled, (state, action) => {
                state.loading = false;
                state.tickets = action.payload?.data;
                state.allTicketsIds = action.payload?.data.map((ticket) => ticket.ticket_id);
                // console.log("Tickets fetched successfully:", action.payload);
            })
            .addCase(getTicketsByEventId.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message;
                // console.error("Failed to fetch tickets:", action.error.message);
            });

        builder
            .addCase(deleteTicketById.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteTicketById.fulfilled, (state, action) => {
                state.loading = false;
                const { ticketId } = action.payload; // Extract ticketId from the payload
                state.tickets = state.tickets.filter((ticket) => ticket.ticket_id !== ticketId);
                // console.log("Ticket deleted successfully:", action.payload);
            })
            .addCase(deleteTicketById.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message;
                console.error('Failed to delete ticket:', action.error.message);
            });

        builder
            .addCase(updateTicketById.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateTicketById.fulfilled, (state, action) => {
                state.loading = false;
                const updatedTicket = action.payload?.data;
                // console.log("Ticket updated successfully:", updatedTicket);
                const index = state.tickets.findIndex(
                    (ticket) => ticket.ticket_id === updatedTicket.ticket_id
                );
                if (index !== -1) {
                    state.tickets[index] = updatedTicket; // Update the ticket in the state
                }
                console.log('Ticket updated successfully:', updatedTicket);
            })
            .addCase(updateTicketById.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message;
                console.error('Failed to update ticket:', action.error.message);
            });

        builder
            .addCase(getTicketById.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getTicketById.fulfilled, (state, action) => {
                state.loading = false;
                state.ticket = action.payload?.data;
                console.log('Ticket fetched successfully:', action.payload);
            })
            .addCase(getTicketById.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message;
                console.error('Failed to fetch ticket:', action.error.message);
            });

        // Add this to your extraReducers builder (after the existing ones)
        builder
            .addCase(deleteAllTicketById.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteAllTicketById.fulfilled, (state, action) => {
                // state.loading = false;
                // Remove all deleted tickets from state
                // state.tickets = [];
                // console.log('All tickets deleted successfully:', action.payload);

                state.loading = false;
                const { ticketIds } = action.payload; // Get selected IDs from payload
                if (Array.isArray(ticketIds) && ticketIds.length > 0) {
                    state.tickets = state.tickets.filter(
                        (ticket) => !ticketIds.includes(ticket.ticket_id)
                    );
                }
            })
            .addCase(deleteAllTicketById.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message;
                console.error('Failed to delete tickets:', action.error.message);
            });
    }
});

export const {
    redirectToTicket,
    showTicketFormUi,
    showTicketListUi,
    setSelectedTicket,
    clearSelectedTicketState
} = ticketSlice.actions;

export default ticketSlice.reducer;

export const createTicketByEventId = createAsyncThunk(
    'tickets/createTicketByEventId',
    async (ticketData, { rejectWithValue }) => {
        try {
            const response = await axios.post(`/api/v2/tickets`, ticketData);
            successToastTopRight('Ticket created successfully');
            return response.data;
        } catch (error) {
            console.error('Failed to create ticket:', error);
            errorToast(error?.data?.message || 'Failed to create ticket');
            return rejectWithValue(error.response.data);
        }
    }
);

export const getTicketById = createAsyncThunk(
    'tickets/getTicketById',
    async (ticketId, { rejectWithValue }) => {
        try {
            const response = await axios.get(`/api/v2/tickets/${ticketId}`);
            return response.data;
        } catch (error) {
            errorToast(error?.response?.data?.message || 'Failed to fetch ticket');
            return rejectWithValue(error.response.data);
        }
    }
);

export const updateTicketById = createAsyncThunk(
    'tickets/updateTicketById',
    async ({ ticketId, ticketData }, { rejectWithValue }) => {
        try {
            const response = await axios.put(`/api/v2/tickets/${ticketId}`, ticketData);
            // successToastTopRight('Ticket updated successfully');
            return response.data;
        } catch (error) {
            return rejectWithValue({
                ...error?.data,
                code: error?.status
            });
        }
    }
);

export const getTicketsByEventId = createAsyncThunk(
    'tickets/getTicketsByEventId',
    async (eventId, { rejectWithValue }) => {
        try {
            const backedResponse = await axios.get(`/api/v2/tickets/event/${eventId}`);

            const response = {
                ...backedResponse?.data,
                code: backedResponse?.status
            }

            return response
        } catch (error) {
            // errorToast(error?.data?.message || "Failed to fetch tickets");
            return rejectWithValue(error?.data);
        }
    }
);

export const deleteTicketById = createAsyncThunk(
    'tickets/deleteTicketById',
    async (ticketId, { rejectWithValue }) => {
        try {
            const response = await axios.delete(`/api/v2/tickets/${ticketId}`);
            successToastTopRight('Ticket deleted successfully');
            return { ticketId, ...response.data };
        } catch (error) {
            errorToast(error?.response?.data?.message || 'Failed to delete ticket');
            return rejectWithValue(error.response.data);
        }
    }
);

export const deleteAllTicketById = createAsyncThunk(
    'tickets/deleteAllTicketById',
    async (ticketIds, { rejectWithValue }) => {
        try {
            const response = await axios.delete(`/api/v2/tickets/delete`, {
                data: { ids: ticketIds }
            });
            // successToastTopRight('Ticket deleted successfully');
            return { ticketIds, ...response.data };
        } catch (error) {
            return rejectWithValue({
                ...error?.data,
                code: error?.status
            });
        }
    }
);
